import { ref, reactive, watch } from '@vue/composition-api';
import store from '@/store/index';
import useDate from '@/utils/hooks/useDate';
import usePagination from '@/views/components/pagination/usePagination';
import useFilterHeaderTable from '@/views/components/filter-header-table/useFilterHeaderTable';
import { dynamicTableModel } from '@/models/data-management/dynamic-table';
import { setBannerModel } from '@/models/banner/banner';
import useValidationModal from '@/@core/components/base-modal/hooks/useValidationModal';

export default function useMappingBannerModal(props) {
  const { fetchQueryDataModel } = dynamicTableModel();
  const { fetchSetBannerListModel } = setBannerModel();
  const { pagination } = usePagination();
  const { resolveDateWithSeperator } = useDate();
  const { openDefaultErrorModal } = useValidationModal();

  const {
    entities,
    filters,
    ordering,
    searchValue,
    onSearch,
  } = useFilterHeaderTable();

  const tabActive = ref('bannerList');

  const loading = reactive({
    tableData: false,
  });

  const fields = reactive({
    bannerList: [
      { key: 'bannerid', label: 'No.' },
      { key: 'banner_image_image_url', label: 'Image' },
      { key: 'name', label: 'Banner Name', sortable: true },
      { key: 'call_to_action', label: 'Call to Action' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: '' },
    ],
    setBanner: [
      { key: 'seq', label: 'No.' },
      { key: 'set_name', label: 'Set Name', sortable: true },
      { key: 'valid_from', label: 'Valid From' },
      { key: 'valid_through', label: 'Valid Through' },
      { key: 'updatetime', label: 'Latest Update' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: '' },
    ],
  });

  const mappingBannerTable = reactive({
    payload: [],
    totalRow: 0,
  });

  const mappingSetBannerTable = reactive({
    payload: [],
    totalRow: 0,
  });

  const mappingBannerList = ref([]);

  const fetchBannerMappingList = () => {
    if (tabActive.value == 'bannerList') {
      return fetchBannerList();
    }

    return fetchSetBannerList();
  };

  const fetchBannerList = () => {
    loading.tableData = true;

    fetchQueryDataModel.payload.Page = pagination.currentPage;
    fetchQueryDataModel.payload.Limit = entities.value || pagination.perPage;
    fetchQueryDataModel.payload.Ordering = ordering.value;
    fetchQueryDataModel.payload.Filters = filters.value;
    fetchQueryDataModel.payload.DisplayId = null;
    fetchQueryDataModel.payload.Display_Key = 'Banner_List';
    fetchQueryDataModel.payload.QueryDataId = null;
    fetchQueryDataModel.payload.QueryData_Key = 'Banner_List';
    fetchQueryDataModel.payload.IsVerified = true;

    store
      .dispatch('api/apiRequest', fetchQueryDataModel)
      .then((response) => {
        mappingBannerTable.payload = filterMappingBannerList(
          response.data.payload
        );
        mappingBannerTable.totalRow = response.data.filtered_record;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = mappingBannerTable.totalRow;
      })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const fetchSetBannerList = () => {
    fetchSetBannerListModel.payload.page = pagination.currentPage;
    fetchSetBannerListModel.payload.Filters = filters.value;

    loading.tableData = true;

    store
      .dispatch('api/apiRequest', fetchSetBannerListModel)
      .then((response) => {
        mappingSetBannerTable.payload = filterMappingSetBannerList(
          response.data.payload
        );

        mappingSetBannerTable.totalRow = response.data.filtered_record;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = mappingSetBannerTable.totalRow;
      })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const addMappingBanner = (banner) => {
    const bannerObj = {
      banner_id: banner.bannerid,
      no: banner.bannerid,
      banner_image_url: banner.banner_image_image_url,
      set_banner: null,
      banner_name: banner.name,
      call_to_action: resolveCallToAction(banner.call_to_action),
      valid_from: banner.createdtime,
      valid_through: banner.valid_through,
      updated_time: banner.updatedtime,
      status: banner.status,
      description: banner.description,
    };

    banner.mapping = true;
    mappingBannerList.value.push(bannerObj);
  };

  const addMappingSetBanner = (setBanner) => {
    const setBannerObj = {
      set_banner_id: setBanner.set_banner_id,
      no: setBanner.seq,
      banner_image_url: null,
      set_name: setBanner.set_name,
      banner_name: null,
      call_to_action: null,
      valid_from: setBanner.valid_from,
      valid_through: setBanner.valid_through,
      update_time: setBanner.updatetime,
      status: setBanner.status,
      description: setBanner.description,
    };

    setBanner.mapping = true;
    mappingBannerList.value.push(setBannerObj);
  };

  const resolveCallToAction = (status) => {
    switch (status) {
      case 1:
        return 'None';
      case 2:
        return 'Full Image';
      case 3:
        return 'Landing Page';
      case 4:
        return 'Content Detail';
      case 5:
        return 'Site URL';
      default:
        return 'None';
    }
  };

  const resolveBannerStatusLabel = (status) => {
    switch (status) {
      case true:
        return 'Active';
      case false:
        return 'Inactive';

      default:
        return 'Unknown';
    }
  };

  const onPageChange = (page) => {
    pagination.currentPage = page;
  };

  const onSearchBanner = (searchInput) => {
    searchValue.query = searchInput;
    onSearch();
  };

  const onTabChange = (tab) => {
    switch (tab) {
      case 'bannerList':
        if (tabActive !== 'bannerList') {
          tabActive.value = 'bannerList';
          searchValue.query = '';
          filters.value = [];
          pagination.currentPage = 1;
          pagination.totalRows = mappingBannerTable.totalRow;
        }

        break;

      case 'setBanner':
        if (tabActive !== 'setBanner') {
          tabActive.value = 'setBanner';
          searchValue.query = '';
          filters.value = [];
          pagination.currentPage = 1;
          pagination.totalRows = mappingSetBannerTable.totalRow;
        }
        break;

      default:
        break;
    }
  };

  const filterMappingBannerList = (bannerList) => {
    const filteredBannerList = [...bannerList];

    filteredBannerList.map((banner) => {
      const mappedBanner = mappingBannerList.value.find(
        (mappingBanner) => mappingBanner.banner_id == banner.bannerid
      );

      if (mappedBanner) {
        return (banner.mapping = true);
      }

      banner.mapping = false;
    });

    return filteredBannerList;
  };

  const filterMappingSetBannerList = (setBannerList) => {
    const filteredSetBannerList = [...setBannerList];

    filteredSetBannerList.map((setBanner) => {
      const mappedSetBanner = mappingBannerList.value.find(
        (mappingBanner) =>
          mappingBanner.set_banner_id == setBanner.set_banner_id
      );

      if (mappedSetBanner) {
        return (setBanner.mapping = true);
      }

      setBanner.mapping = false;
    });

    return filteredSetBannerList;
  };

  const initMappingBannerList = () => {
    mappingBannerList.value = [...props.mappingList];
  };

  const onModalOpen = () => {
    initMappingBannerList();
    fetchBannerMappingList();
  };

  watch(
    [entities, filters, ordering],
    () => {
      pagination.currentPage = 1;
      fetchBannerMappingList();
    },
    { deep: true }
  );

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchBannerMappingList();
      }
    },
    { deep: true }
  );

  return {
    tabActive,
    fields,
    mappingBannerTable,
    mappingSetBannerTable,
    pagination,
    loading,
    mappingBannerList,
    resolveBannerStatusLabel,
    resolveCallToAction,
    resolveDateWithSeperator,
    addMappingBanner,
    addMappingSetBanner,
    onPageChange,
    onSearchBanner,
    onTabChange,
    fetchBannerMappingList,
    initMappingBannerList,
    onModalOpen,
  };
}
